/**
 * Auth API client for the custom hybrid token+JWT authentication system.
 * Manages access tokens in memory and refresh tokens via httpOnly cookies.
 */

const API_URL = import.meta.env.VITE_API_URL || '';

// Defensive JSON parser. Some hosts/proxies can strip the body on 4xx/5xx
// or CORS edge cases; treat those as empty objects rather than crashing
// with "Unexpected end of JSON input" before the caller can inspect status.
async function parseResponse<T = any>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return { error: text.slice(0, 300) } as T;
  }
}

let accessToken: string | null = null;
let tokenExpiresAt = 0;
let refreshPromise: Promise<boolean> | null = null;
let onSessionExpired: (() => void) | null = null;

// ─── Token management ────────────────────────────────────────────────

export function setAccessToken(token: string, expiresIn: number): void {
  accessToken = token;
  tokenExpiresAt = Date.now() + expiresIn * 1000;
  scheduleRefresh(expiresIn);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function clearTokens(): void {
  accessToken = null;
  tokenExpiresAt = 0;
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

// ─── Automatic refresh ──────────────────────────────────────────────

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleRefresh(expiresIn: number): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  // Refresh 60 seconds before expiry
  const refreshAt = Math.max((expiresIn - 60) * 1000, 5000);
  refreshTimer = setTimeout(() => {
    refreshAccessToken().catch(() => {
      // Refresh failed — session expired
    });
  }, refreshAt);
}

export async function refreshAccessToken(silent = false): Promise<boolean> {
  // Deduplicate concurrent refresh calls
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // sends httpOnly cookie
      });

      if (!res.ok) {
        clearTokens();
        if (!silent) onSessionExpired?.();
        return false;
      }

      const data = await res.json();
      setAccessToken(data.access_token, data.expires_in);
      return true;
    } catch {
      clearTokens();
      if (!silent) onSessionExpired?.();
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ─── Auth API calls ─────────────────────────────────────────────────

interface AuthResponse {
  access_token: string;
  expires_in: number;
  is_new_user?: boolean;
  _dev_code?: string;
}

interface MessageResponse {
  message: string;
  requires_verification?: boolean;
  _dev_code?: string;
}

export async function register(email: string, password: string, displayName?: string): Promise<MessageResponse> {
  const res = await fetch(`${API_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, display_name: displayName }),
  });
  const data = await res.json();
  if (!res.ok) throw new AuthError(data.error, res.status, data);
  return data;
}

export async function verifyEmail(email: string, code: string): Promise<AuthResponse> {
  const res = await fetch(`${API_URL}/auth/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, code }),
  });
  const data = await res.json();
  if (!res.ok) throw new AuthError(data.error, res.status, data);
  setAccessToken(data.access_token, data.expires_in);
  return data;
}

export async function login(email: string, password: string): Promise<AuthResponse> {
  const res = await fetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new AuthError(data.error, res.status, data);
  setAccessToken(data.access_token, data.expires_in);
  return data;
}

export async function logout(): Promise<void> {
  try {
    await fetchWithAuth(`${API_URL}/auth/logout`, { method: 'POST', credentials: 'include' });
  } finally {
    clearTokens();
  }
}

export async function logoutAll(): Promise<{ revoked_count: number }> {
  const res = await fetchWithAuth(`${API_URL}/auth/logout-all`, { method: 'POST', credentials: 'include' });
  const data = await res.json();
  clearTokens();
  return data;
}

export async function forgotPassword(email: string): Promise<MessageResponse> {
  const res = await fetch(`${API_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await parseResponse<MessageResponse & { error?: string }>(res);
  if (!res.ok) {
    throw new AuthError(
      data.error || `Le serveur a répondu ${res.status} sans message.`,
      res.status,
      data
    );
  }
  return data;
}

export async function resetPassword(email: string, code: string, newPassword: string): Promise<MessageResponse> {
  const res = await fetch(`${API_URL}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, new_password: newPassword }),
  });
  const data = await res.json();
  if (!res.ok) throw new AuthError(data.error, res.status, data);
  return data;
}

// ─── Google OAuth ───────────────────────────────────────────────────

export async function getGoogleOAuthUrl(redirectUri: string): Promise<string> {
  // Generate PKCE challenge (stays on client so code_verifier never leaves the browser)
  const codeVerifier = generateCodeVerifier();
  const state = generateState();
  sessionStorage.setItem('oauth_code_verifier', codeVerifier);
  sessionStorage.setItem('oauth_redirect_uri', redirectUri);
  sessionStorage.setItem('oauth_state', state);

  // Let the backend construct the full URL using its GOOGLE_CLIENT_ID env var
  const params = new URLSearchParams({ redirect_uri: redirectUri, code_challenge: codeVerifier, state });
  const res = await fetch(`${API_URL}/auth/oauth/google/init?${params.toString()}`);
  const data = await parseResponse<{ url?: string; error?: string }>(res);
  if (!res.ok) {
    throw new Error(data.error || `Google OAuth unavailable (HTTP ${res.status})`);
  }
  if (!data.url) {
    throw new Error('Réponse du serveur invalide.');
  }
  return data.url;
}

export async function handleGoogleCallback(code: string): Promise<AuthResponse> {
  const codeVerifier = sessionStorage.getItem('oauth_code_verifier');
  const redirectUri = sessionStorage.getItem('oauth_redirect_uri');

  // Clean up
  sessionStorage.removeItem('oauth_code_verifier');
  sessionStorage.removeItem('oauth_redirect_uri');
  sessionStorage.removeItem('oauth_state');

  if (!codeVerifier || !redirectUri) {
    throw new Error('OAuth state missing. Please try again.');
  }

  const res = await fetch(`${API_URL}/auth/oauth/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ code, code_verifier: codeVerifier, redirect_uri: redirectUri }),
  });

  const data = await res.json();
  if (!res.ok) throw new AuthError(data.error, res.status, data);
  setAccessToken(data.access_token, data.expires_in);
  return data;
}

// ─── Apple OAuth ────────────────────────────────────────────────────

export async function getAppleOAuthUrl(redirectUri: string): Promise<string> {
  const state = generateState();
  sessionStorage.setItem('oauth_state', state);
  sessionStorage.setItem('oauth_redirect_uri', redirectUri);

  const params = new URLSearchParams({ redirect_uri: redirectUri, state });
  const res = await fetch(`${API_URL}/auth/oauth/apple/init?${params.toString()}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as any).error || 'Apple OAuth unavailable');
  }
  const data = await res.json();
  return (data as any).url;
}

export async function handleAppleCallback(code: string, userName?: string): Promise<AuthResponse> {
  const redirectUri = sessionStorage.getItem('oauth_redirect_uri');
  sessionStorage.removeItem('oauth_redirect_uri');
  sessionStorage.removeItem('oauth_state');

  if (!redirectUri) {
    throw new Error('OAuth state missing. Please try again.');
  }

  const res = await fetch(`${API_URL}/auth/oauth/apple`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ code, redirect_uri: redirectUri, user_name: userName }),
  });

  const data = await res.json();
  if (!res.ok) throw new AuthError(data.error, res.status, data);
  setAccessToken(data.access_token, data.expires_in);
  return data;
}

// ─── Authenticated fetch helper ─────────────────────────────────────

export async function fetchWithAuth(url: string, init: RequestInit = {}): Promise<Response> {
  // If token is expired or about to expire, try refresh first
  if (!accessToken || Date.now() >= tokenExpiresAt - 30000) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      throw new AuthError('Session expired', 401);
    }
  }

  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${accessToken}`);

  const res = await fetch(url, { ...init, headers });

  // If 401, attempt one refresh and retry
  if (res.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      headers.set('Authorization', `Bearer ${accessToken}`);
      return fetch(url, { ...init, headers });
    }
  }

  return res;
}

// ─── Sessions management ────────────────────────────────────────────

export async function getSessions() {
  const res = await fetchWithAuth(`${API_URL}/auth/sessions`);
  if (!res.ok) throw new AuthError('Failed to fetch sessions', res.status);
  return res.json();
}

export async function revokeRemoteSession(sessionId: string) {
  const res = await fetchWithAuth(`${API_URL}/auth/sessions/${sessionId}`, { method: 'DELETE' });
  if (!res.ok) throw new AuthError('Failed to revoke session', res.status);
  return res.json();
}

// ─── Helpers ────────────────────────────────────────────────────────

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

function generateState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

export class AuthError extends Error {
  status: number;
  data?: any;
  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'AuthError';
    this.status = status;
    this.data = data;
  }
}
